import { useState, useEffect } from 'react'
import { Download } from 'lucide-react'

// Extend window interface for BeforeInstallPromptEvent
declare global {
  interface Window {
    deferredPrompt?: BeforeInstallPromptEvent
  }
}

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed'
    platform: string
  }>
  prompt(): Promise<void>
}

const InstallPWAButton = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isInstallable, setIsInstallable] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)

  useEffect(() => {
    const handler = (e: BeforeInstallPromptEvent) => {
      e.preventDefault()
      window.deferredPrompt = e
      setDeferredPrompt(e)
      setIsInstallable(true)
    }

    // Listen for beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', handler as EventListener)

    // Check if app is already installed
    const checkInstalled = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      const nav = window.navigator as unknown as { standalone?: boolean }
      const isInstalled = 'standalone' in nav && nav.standalone
      setIsInstalled(isStandalone || isInstalled || false)
    }

    checkInstalled()

    // Listen for app installed
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true)
      setIsInstallable(false)
      setDeferredPrompt(null)
    })

    return () => {
      window.removeEventListener('beforeinstallprompt', handler as EventListener)
    }
  }, [])

  // Register service worker
  useEffect(() => {
    if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
      navigator.serviceWorker.register('/sw.js')
        .then(registration => {
          console.log('Service Worker registrado com sucesso:', registration)
        })
        .catch(error => {
          console.error('Falha ao registrar Service Worker:', error)
        })
    }
  }, [])

  const handleInstallClick = async () => {
    if (!deferredPrompt) return

    // Show the install prompt
    await deferredPrompt.prompt()

    // Wait for the user to respond to the prompt
    const choiceResult = await deferredPrompt.userChoice

    if (choiceResult.outcome === 'accepted') {
      console.log('Usuário aceitou a instalação')
      setIsInstalled(true)
    } else {
      console.log('Usuário recusou a instalação')
    }

    // Reset the deferred prompt variable
    setDeferredPrompt(null)
    setIsInstallable(false)
  }

  // Don't show button if already installed or not installable
  if (isInstalled || !isInstallable) {
    return null
  }

  return (
    <button
      onClick={handleInstallClick}
      className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 bg-noe-brand text-noe-950 font-medium rounded-full shadow-lg hover:bg-noe-brand/90 transition-colors animate-bounce"
      title="Instalar aplicativo"
    >
      <Download className="w-5 h-5" />
      <span className="hidden sm:inline">Instalar App</span>
    </button>
  )
}

export default InstallPWAButton